import { ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useLightTheme, useTerminalTheme } from '../theme/terminalTheme'
import { useThemePreference } from '../theme/themePreference'
import { SurfaceThemeProvider } from '../theme/surfaceTheme'
import useKrakenAccess from '../hooks/useKrakenAccess'
import AssistantChat from './AssistantChat'
import AppFooter from './AppFooter'
import Logo from './Logo'
import NotificationBell from './NotificationBell'
import Phase2MilestoneBanner from './Phase2MilestoneBanner'
import { trackRecordHref } from './trackRecord/recordSource'
import { FEE_CALCULATOR_PATH, MY_CALLS_PATH } from '../routes'
import {
  DocumentTextIcon,
  HomeIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  BanknotesIcon,
  CreditCardIcon,
  TrophyIcon,
  BoltIcon,
  Cog6ToothIcon,
  ArrowsRightLeftIcon,
  LinkIcon,
  AcademicCapIcon,
  BookOpenIcon,
  BellAlertIcon,
  CpuChipIcon,
  FlagIcon,
} from '@heroicons/react/24/outline'

interface LayoutProps {
  children: ReactNode
}

interface NavItem {
  name: string
  href: string
  icon: typeof HomeIcon
  /** Other paths (and their sub-paths) that count as this item: pages reached
   *  from it rather than from the sidebar, e.g. /create for Content's Create
   *  tab, /calls from Signals. */
  alsoActiveOn?: string[]
}

// Routes kept but deliberately not in the sidebar: /marketplace, /leaderboard,
// /ab-testing, /live-room, /scheduled, /admin/vetting,
// /admin/exit-backtest (admin analysis, reached from Settings -> Engine),
// /analytics (until the record has 200+ closes; its figures are on the
// Dashboard and Track Record meanwhile). Reached from a page rather than the
// sidebar: the Paper record (Track Record's Engine / Paper switch), the fee
// calculator (linked from Track Record), My calls (linked from Signals) and
// the theme control (Settings).

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Every signed-in page shares the Dashboard's dark terminal palette unless
  // the user picked light (or Auto on a light device), which swaps in the
  // light palette; the `dark:` classes below take effect while dark is on.
  const { resolved } = useThemePreference()
  useTerminalTheme(resolved === 'dark')
  useLightTheme(resolved === 'light')

  const canTrade = useKrakenAccess()

  const isAdmin = Boolean(user?.is_superuser)

  // Everyone's places first; admin-only tools in their own group at the end,
  // so a regular user's menu and an admin's read the same down to it.
  const groups: { label: string; items: NavItem[] }[] = [
    {
      label: 'Core',
      items: [
        { name: 'Dashboard', href: '/', icon: HomeIcon },
        { name: 'Signals', href: '/signals', icon: BoltIcon, alsoActiveOn: [MY_CALLS_PATH] },
        { name: 'Track Record', href: trackRecordHref('engine'), icon: TrophyIcon, alsoActiveOn: [FEE_CALCULATOR_PATH] },
        // Create is a tab of Content; /create opens it.
        { name: 'Content', href: '/content', icon: DocumentTextIcon, alsoActiveOn: ['/create'] },
      ],
    },
    {
      label: 'Growth',
      items: [
        { name: 'Feed', href: '/feed', icon: UserGroupIcon },
        { name: 'Creators', href: '/creators', icon: MagnifyingGlassIcon },
        // Courses you teach; a course's own page is public at /learn/<slug>.
        { name: 'Courses', href: '/courses', icon: BookOpenIcon },
        { name: 'Monetization', href: '/monetization', icon: BanknotesIcon },
        { name: 'Subscriptions', href: '/subscriptions', icon: CreditCardIcon },
      ],
    },
    {
      label: 'Account',
      items: [
        // Alert rules; what they fire lands in the bell.
        { name: 'Alerts', href: '/alerts', icon: BellAlertIcon },
        { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
      ],
    },
    {
      label: 'Admin',
      items: [
        // Trades, Practice and Connections sit behind the same gate the server
        // enforces on /api/kraken/* (superuser + KRAKEN_ALLOWED_EMAILS).
        ...(canTrade ? [{ name: 'Trades', href: '/trades', icon: ArrowsRightLeftIcon }] : []),
        ...(canTrade ? [{ name: 'Practice', href: '/practice', icon: AcademicCapIcon }] : []),
        ...(canTrade ? [{ name: 'Connections', href: '/exchange', icon: LinkIcon }] : []),
        ...(isAdmin ? [{ name: 'Engine', href: '/settings/engine', icon: CpuChipIcon }] : []),
        // Reported course reviews and discussion posts.
        ...(isAdmin ? [{ name: 'Reports', href: '/admin/reports', icon: FlagIcon }] : []),
      ],
    },
  ].filter((group) => group.items.length > 0)

  const under = (path: string, base: string) => path === base || path.startsWith(`${base}/`)

  // Track Record's href carries no query, so the Paper record (?record=paper)
  // is Track Record too. Settings is not active on its Engine sub-page, which
  // has its own item.
  const isActive = (href: string, alsoActiveOn: string[] = []) => {
    const path = location.pathname
    if (alsoActiveOn.some((base) => under(path, base))) return true
    const base = href.split('?')[0]
    if (base === '/') return path === '/'
    if (base === '/settings') return path === '/settings'
    return under(path, base)
  }

  const closeMobileMenu = () => setIsMobileMenuOpen(false)

  const iconButtonClass =
    'p-2 text-gray-600 hover:text-gray-800 rounded-md hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-100'

  return (
    <SurfaceThemeProvider value={resolved}>
    <div className="min-h-screen bg-gray-50 dark:bg-terminal-bg dark:text-gray-100">
      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white shadow-md z-40 flex items-center px-4 dark:border-b dark:border-terminal-border dark:bg-terminal-surface dark:shadow-black/40">
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className={iconButtonClass}
          aria-label="Open menu"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
        <Logo size="md" className="ml-3" />
        <div className="ml-auto">
          <NotificationBell panelAlign="right" />
        </div>
      </div>

      {/* Mobile backdrop */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-gray-600 bg-opacity-75 z-40 dark:bg-black/70"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 w-64 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out dark:border-r dark:border-terminal-border dark:bg-terminal-surface dark:shadow-none ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0`}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-6 border-b dark:border-terminal-border">
            <Logo />
            {/* Close button for mobile */}
            <button
              onClick={closeMobileMenu}
              className={`md:hidden ${iconButtonClass}`}
              aria-label="Close menu"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Main">
            {groups.map((group) => (
              <div key={group.label} className="mb-4 last:mb-0">
                <p className="mb-1 px-3 text-[11px] font-medium lowercase tracking-wider text-gray-500 [font-variant:small-caps] dark:text-gray-500">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const active = isActive(item.href, item.alsoActiveOn)
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={closeMobileMenu}
                        aria-current={active ? 'page' : undefined}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          active
                            ? 'bg-primary-50 text-primary-600 dark:bg-terminal-raised dark:text-primary-400'
                            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-gray-100'
                        }`}
                      >
                        <item.icon className="mr-3 h-5 w-5" />
                        {item.name}
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User section. The theme control is in Settings. */}
          <div className="border-t p-4 dark:border-terminal-border">
            <div className="flex items-center justify-between">
              <Link
                to="/profile"
                onClick={closeMobileMenu}
                className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition"
              >
                <UserCircleIcon className="h-5 w-5 text-gray-400 shrink-0 dark:text-gray-500" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-100">{user?.username}</p>
                  <p className="text-xs text-gray-500 truncate dark:text-gray-400">{user?.email}</p>
                </div>
              </Link>
              <div className="ml-3 flex items-center">
                {/* Desktop only: on mobile the header has the bell. Pinned to
                    the bottom of the sidebar, so the panel opens upward;
                    opening down would put it below the viewport. */}
                <div className="hidden md:block" data-testid="sidebar-bell">
                  <NotificationBell panelAlign="left" panelPlacement="above" />
                </div>
                <button onClick={logout} className={iconButtonClass} title="Logout">
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="pt-16 md:pt-0 md:pl-64">
        {/* The assistant launcher is fixed to the bottom-right corner (16px
            in, 64px wide). From md up the content stops short of its column
            (pr-24 = 96px), so no control ever slides under it -- a draft's
            Publish button at the right end of a row did. Below md the content
            needs the width; pb-24 leaves room to scroll anything clear. */}
        <main className="pt-6 pb-24 px-4 md:pl-8 md:pr-24">
          <Phase2MilestoneBanner />
          {children}
          <AppFooter />
        </main>
      </div>

      <AssistantChat />
    </div>
    </SurfaceThemeProvider>
  )
}

export default Layout
